import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ActionsCont,
  ChangeLangCont,
  ContentCont,
  DownloadLink,
  Header,
  Heading,
  Logo,
  Main,
  MainCont,
  SpinnerCont,
  SubHeading,
} from './IndividualReport.styles';
import ChangeLangDrop from '../../components/ChangeLangDrop/ChangeLangDrop';
import IndividualReportPDF from '../../components/PDFs/IndividualReportPDF/IndividualReportPDF';
import Spinner from '../../components/Spinner/Spinner';
import { RouteNames } from '../../constans/routes';
import { useIndividualReport, useTemporaryAuth } from '../../hooks';
import {
  IGetIndividualReport200Resp,
  IGetIndividualReportError,
} from '../../services/individualReport';
import Page403 from '../403/403';
import IntroductionSection from './IntroductionSection/IntroductionSection';
import KeyTakeawaysSection from './KeyTakeawaysSection/KeyTakeawaysSection';
import PerceivedWorkEnvironmentSection from './PerceivedWorkEnvironmentSection/PerceivedWorkEnvironmentSection';
import PredictiveStatesSection from './PredictiveStatesSection/PredictiveStatesSection';
import ReflectionTaskSection from './ReflectionTaskSection/ReflectionTaskSection';
import SuggestionsSection from './SuggestionsSection/SuggestionsSection';

const IndividualReport = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { user, getUser } = useTemporaryAuth({ token });
  const { currentLang, getIndividualReport } = useIndividualReport();

  const [response, setResponse] = useState<IGetIndividualReport200Resp | null>(null);
  const [error, setError] = useState<IGetIndividualReportError | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { data, message, respondent, survey } = response ?? {};
  const {
    introduction,
    individual_states: individualStates,
    key_takeaways: keyTakeaways,
    work_environment: workEnvironment,
    prm_code: prmCode,
  } = data ?? {};

  const { details, status } = error ?? {};

  useEffect(() => {
    setIsLoading(true);

    (async () => {
      try {
        await getUser();
      } catch (error) {
        setError(error.data);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (user) {
      (async () => {
        setIsLoading(true);

        try {
          const response = await getIndividualReport();

          setResponse(response);
        } catch (error) {
          if (error.data?.status === 404) {
            navigate(RouteNames.Page404);
          }

          setError(error.data);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [currentLang, user]);

  if (!data && isLoading) {
    return (
      <SpinnerCont id="SpinnerCont" data-testid="spinner">
        <Spinner size="large" />
      </SpinnerCont>
    );
  }

  if (error && status && [401, 403].includes(status)) {
    return <Page403 message={details} {...{ status }} />;
  }

  if (!data && message) {
    return <Page403 status={403} {...{ message }} />;
  }

  return (
    <>
      <Header id="Header">
        <Logo id="Logo" role="img" title="Logo" />
        <ContentCont id="ContentCont">
          <Heading as="h1">{survey}</Heading>
          <SubHeading as="h2">
            {t('results.report.respondent', { data: { respondent } })}
          </SubHeading>
        </ContentCont>
        <ActionsCont id="ActionsCont">
          {response ? (
            <DownloadLink
              data-testid="download"
              document={<IndividualReportPDF language={currentLang} {...{ response }} />}
              fileName={`Communiq ${respondent} ${currentLang}.pdf`}
            >
              {({ loading }) => (loading ? <Spinner size="small" /> : t('Download'))}
            </DownloadLink>
          ) : null}
          <ChangeLangCont id="ChangeLangCont" data-testid="language-switch">
            <ChangeLangDrop positionX="-72px" width="208px" />
          </ChangeLangCont>
        </ActionsCont>
      </Header>
      <Main id="Main">
        <MainCont id="MainCont">
          {introduction ? <IntroductionSection {...{ introduction }} /> : null}
          {workEnvironment ? <PerceivedWorkEnvironmentSection {...{ workEnvironment }} /> : null}
          {individualStates ? <PredictiveStatesSection {...{ individualStates }} /> : null}
          {keyTakeaways ? <KeyTakeawaysSection {...{ keyTakeaways }} /> : null}
          <ReflectionTaskSection />
          {prmCode ? <SuggestionsSection {...{ prmCode }} /> : null}
        </MainCont>
      </Main>
    </>
  );
};

export default IndividualReport;
